import React, { useState } from 'react';
import Header from './Header';
import Footer from './Footer';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

const Companies = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSector, setFilterSector] = useState('all');
  const navigate = useNavigate();

  // Fetch companies with React Query
  const fetchCompanies = async () => {
    const response = await fetch('https://ghanish.in/company_listview/');
    if (!response.ok) {
      throw new Error(`Error fetching companies: ${response.status}`);
    }
    return response.json();
  };

  const { data: companies = [], isLoading, error: queryError } = useQuery({
    queryKey: ['companies'],
    queryFn: fetchCompanies,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 2,
    onError: (err) => {
      console.error("Error fetching companies:", err);
    }
  });
  
  // Extract unique sectors for filtering
  const sectors = React.useMemo(() => {
    if (!companies.length) return [];
    return [...new Set(companies.map(company => company.sector))];
  }, [companies]);

  // Filter companies based on search term and sector
  const filteredCompanies = React.useMemo(() => {
    return companies.filter(company => {
      const matchesSearch = company.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         company.sector.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesSector = filterSector === 'all' || company.sector === filterSector;
      return matchesSearch && matchesSector;
    });
  }, [companies, searchTerm, filterSector]);

  // Get random color for company logo background
  const getRandomColor = (companyId) => {
    const colors = ['#f0f9ff'];
    return colors[companyId % colors.length];
  };

  const slugify = (str) =>
    str.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^\w\-]+/g, '');
  
  const handleCompanyClick = (companyId, companyName) => {
    const slug = slugify(companyName);
    navigate(`/company/${slug}`);
  };

  return (
    <>
      <Helmet>
        <title>Top Companies | Fresher Job Adda</title>
        <meta name="description" content="Browse top companies hiring now" />
      </Helmet>

      <Header />
      
      {/* Hero Banner */}
      <section className="py-5 mb-4" style={{ backgroundColor: "#f1f5f9" }}>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-4 mb-lg-0">
              <h1 className="display-6 fw-bold">Explore Leading Companies</h1>
              <p className="lead">Search, filter, and find top hiring companies in your sector of interest.</p>
            </div>
            <div className="col-lg-6">
              <div className="card border-0 shadow-sm">
                <div className="card-body">
                  <div className="mb-3">
                    <input
                      type="text"
                      className="form-control form-control-lg"
                      placeholder="Search companies "
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    <button
                      className={`btn btn-sm rounded-pill px-3 ${filterSector === 'all' ? 'btn-success' : 'btn-outline-secondary'}`}
                      onClick={() => setFilterSector('all')}
                    >
                      All
                    </button>
                    {sectors.map((sector, idx) => (
                      <button
                        key={idx}
                        className={`btn btn-sm rounded-pill px-3 ${filterSector === sector ? 'btn-success' : 'btn-outline-secondary'}`}
                        onClick={() => setFilterSector(sector)}
                      >
                        {sector}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <section className="py-5" style={{ backgroundColor: "#f8f9fa" }}>
        <div className="container">
          {isLoading ? (
            <div className="row justify-content-center">
              <div className="col-12 text-center py-5">
                <div className="spinner-border text-success" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading companies...</p>
              </div>
            </div>
          ) : queryError ? (
            <div className="row justify-content-center">
              <div className="col-12 text-center py-5">
                <div className="alert alert-danger">
                  <i className="fa-solid fa-circle-exclamation me-2"></i>
                  {queryError.message}
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="row mb-4">
                <div className="col-12">
                  <h2 className="mb-0">
                    {filterSector === 'all' 
                      ? 'All Companies' 
                      : `${filterSector} Companies`}
                  </h2>
                  <p className="text-muted">
                    {filteredCompanies.length} {filteredCompanies.length === 1 ? 'company' : 'companies'} found
                  </p>
                </div>
              </div>
            
              <div className="row">
                {filteredCompanies.length === 0 ? (
                  <div className="col-12 text-center py-5">
                    <div className="alert alert-info">
                      <i className="fa-solid fa-info-circle me-2"></i>
                      No companies found matching your criteria
                    </div>
                  </div>
                ) : (
                  filteredCompanies.map((company) => (
                    <div key={company.company_id} className="col-md-6 col-lg-3 mb-4" role='button' onClick={() => handleCompanyClick(company.company_id, company.name)}>
                      <div className="card h-100 shadow-sm hover-shadow border-0">
                        <div 
                          className="position-relative"
                          style={{ 
                            height: "160px",
                            overflow: "hidden"
                          }}
                        >
                          {/* Company name badge at top right */}
                          <div 
                            className="position-absolute"
                            style={{
                              top: "10px",
                              right: "10px",
                              zIndex: 3
                            }}
                          >
                            <span className="badge bg-success text-white px-3 py-2 shadow-sm">
                              {company.name}
                            </span>
                          </div>

                          {/* Fallback icon - always present but hidden by default */}
                          <div 
                            className="d-flex flex-column align-items-center justify-content-center fallback-icon"
                            style={{ 
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              backgroundColor: getRandomColor(company.company_id),
                              display: "none"
                            }}
                          >
                            <div 
                              className="company-logo bg-white rounded-circle mb-2"
                              style={{ 
                                width: "70px",
                                height: "70px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                border: "2px solid #fff",
                                boxShadow: "0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)"
                              }}
                            >
                              <i className="fa-solid fa-building text-success" style={{ fontSize: '28px' }}></i>
                            </div>
                          </div>

                          {/* Company image */}
                          {company.image_url ? (
                            <img
                              src={company.image_url}
                              alt={company.name}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                borderTopLeftRadius: "0.375rem",
                                borderTopRightRadius: "0.375rem"
                              }}
                              loading="lazy"
                              onError={(e) => {
                                e.target.style.display = "none";
                                const fallbackIcon = e.target.parentNode.querySelector(".fallback-icon");
                                if (fallbackIcon) {
                                  fallbackIcon.style.display = "flex";
                                }
                              }}
                              className="company-logo"
                            />
                          ) : (
                            <div className="fallback-icon" style={{ display: "flex" }}>
                              <div 
                                className="company-logo bg-white rounded-circle mb-2"
                                style={{ 
                                  width: "70px",
                                  height: "70px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  border: "2px solid #fff",
                                  boxShadow: "0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)"
                                }}
                              >
                                <i className="fa-solid fa-building text-success" style={{ fontSize: '28px' }}></i>
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="card-body p-4">
                          <div className="d-flex justify-content-between mb-3">
                            <div>
                              <span className="badge bg-light text-dark border mb-2 d-inline-block">
                                <i className="fa-solid fa-industry me-1"></i> 
                                {company.sector}
                              </span>
                            </div>
                            {/* <div className="text-end">
                              {company.is_mnc === 1 && (
                                <span className="badge bg-primary ms-1 mb-2 d-inline-block">MNC</span>
                              )}
                            </div> */}
                             <div className='text-center utf_apply_job_btn_item'>
                            <Link 
                              to={`/company/${company.name.toLowerCase().replace(/\s+/g, '-')}`} 
                               className="btn job-browse-btn br-light"
                            >
                              <i className="fa-solid fa-briefcase me-1"></i> Jobs
                            </Link>
                          </div>
                          </div>
                          
                          {/* <hr className="my-3" /> */}
                          
                         
                          
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </section>

      <Footer />

      <style jsx>{`
        .card {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          overflow: hidden;
        }
        
        .card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 16px rgba(0,0,0,0.1) !important;
        }
        
        .company-logo {
          transition: transform 0.3s ease;
          will-change: transform;
        }
        
        .card:hover .company-logo {
          transform: scale(1.05);
        }

        .position-relative {
          overflow: hidden;
        }

        .fallback-icon {
          transition: opacity 0.3s ease;
        }

        .card:hover .fallback-icon {
          opacity: 0.9;
        }

        .card:hover img.company-logo {
          transform: scale(1.05);
          transition: transform 0.3s ease;
        }

        .badge {
          transition: opacity 0.3s ease;
        }

        .card:hover .badge {
          opacity: 0.9;
        }
      `}</style>
    </>
  );
};

export default Companies; 